import MarkdownFormatter from "@/components/common/markdown/MarkdownFormatter";
import { Button } from "@/components/ui/button";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DropdownMenu } from "@radix-ui/react-dropdown-menu";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { LucideAlertCircle } from "lucide-react";
import { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { LuCode, LuCopy } from "react-icons/lu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RiPencilLine,
  RiGeminiFill,
  RiGeminiLine,
  RiExpandLeftRightLine,
} from "@remixicon/react";
import { SqButton } from "../squircle-ui/button";
import { File } from "@/types";
import { FileAttachmentsPreview } from "@/components/hoc/fileAttachment";
import { fetchInsightPrompts } from "@/screens/creatives/apiService";
import { useGlobalStore } from "@/GlobalStore";
import { InsightType } from "@/screens/creatives/types";

/**
 * Normalize raw insight text into clean markdown.
 * Converts Unicode bullets (•, ●) to `- `, en/em-dashes (–, —) to nested `  - `,
 * and promotes short standalone lines before lists into ### headings.
 */
const normalizeInsightMarkdown = (raw: string): string => {
  const lines = raw.split("\n");
  const out: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Convert lines starting with bullet characters to markdown list items
    if (/^\s*[•●]\s+/.test(line)) {
      line = line.replace(/^\s*[•●]\s+/, "- ");
    }
    // Convert lines starting with en-dash / em-dash to nested list items
    else if (/^\s*[–—]\s+/.test(line)) {
      line = line.replace(/^\s*[–—]\s+/, "  - ");
    }
    // Promote short standalone text lines (before a list) into headings
    // Only if: line is not empty, not already a heading, not a list item,
    // not an image/link, is short (<80 chars), and the next non-empty line starts a list
    else if (
      line.trim() &&
      !line.trim().startsWith("#") &&
      !line.trim().startsWith("-") &&
      !line.trim().startsWith("*") &&
      !line.trim().startsWith("!") &&
      !/^\d+\.\s/.test(line.trim()) &&
      !/!\[.*\]\(.*\)/.test(line) &&
      !/\[.*\]\(.*\)/.test(line) &&
      line.trim().length < 80
    ) {
      // Look ahead for a list starting soon
      let nextContentLine = "";
      for (let j = i + 1; j < lines.length && j <= i + 2; j++) {
        if (lines[j].trim()) {
          nextContentLine = lines[j];
          break;
        }
      }
      const nextIsListOrBullet =
        /^\s*[•●–—-]\s+/.test(nextContentLine) || /^\s*\d+\.\s/.test(nextContentLine);

      if (nextIsListOrBullet) {
        line = `### ${line.trim()}`;
      }
    }

    out.push(line);
  }

  return out.join("\n");
};

interface Props {
  insights: string[];
  setInsights: (insights: string[]) => void;
  type: InsightType;
  loading: boolean;
  hideGenerateButton?: boolean;
  generateInsight: (promptOrId: { prompt_id?: string; prompt?: string }) => void;
  accountId?: string;
  initialInsight: string;
  initialPrompt: string;
  error: string;
  collapsible?: boolean;
}

// Exposed methods interface
export interface ReportInsightsRef {
  setHasGeneratedInsights: (value: boolean) => void;
  setCustomPrompt: (value: string) => void;
  setLastUsedPrompt: (value: string | null) => void;
  setOutdatedInsight: (value: boolean) => void;
}

const getMediaType = (url: string): "image" | "video" | null => {
  if (
    /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v|3gp|ogv)(\?|$)/i.test(url) ||
    url.includes("video") ||
    url.includes("vid")
  ) {
    return "video";
  }
  if (
    /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff?)(\?|$)/i.test(url) ||
    url.includes("image") ||
    url.includes("img")
  ) {
    return "image";
  }
  if (/^https?:\/\//.test(url)) {
    return "image";
  }
  return null;
};

const extractMedia = (
  content: string,
): { url: string; alt: string; type: "image" | "video" }[] => {
  const mediaRegex = /(?:!\[([^\]]*)\]|(\[([^\]]*)\]))\(([^)]+)\)/g;
  const media: { url: string; alt: string; type: "image" | "video" }[] = [];
  let match;

  while ((match = mediaRegex.exec(content)) !== null) {
    const url = match[4];
    const alt = match[1] || match[3] || "";

    if (url) {
      const type = getMediaType(url);
      if (type) {
        media.push({ url, alt, type });
      }
    }
  }
  return media;
};

const convertMediaToFiles = (
  media: { url: string; alt: string; type: "image" | "video" }[],
  idPrefix = "",
): File[] => {
  return media.map((item, index) => ({
    id: `extracted-media-${idPrefix}${index}`,
    file_name: item.alt || `${item.type === "image" ? "Image" : "Video"} ${index + 1}`,
    public_url: item.url,
  }));
};

type MediaItem = { url: string; alt: string; type: "image" | "video" };

const isMediaUrl = (url: string): boolean => getMediaType(url) !== null;

/**
 * Strip only markdown image syntax ![alt](url) that points to media URLs,
 * preventing MarkdownFormatter from rendering duplicate inline <img> tags.
 * Link syntax [text](url) is preserved so ad names remain as colored links.
 */
const stripMediaImages = (content: string): string => {
  return content.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (fullMatch, alt, url) => {
    if (url && isMediaUrl(url)) {
      return alt || "";
    }
    return fullMatch;
  });
};

/**
 * Splits insight content into segments so that media previews can be rendered
 * right after the bullet/section that references them, rather than all grouped
 * at the end.  When a media link appears inside a sub-bullet, the split is
 * deferred until the whole bullet group finishes (next non-indented line) so
 * that sub-bullet structure is preserved.
 */
const splitContentAtMediaLines = (
  content: string,
): { text: string; media: MediaItem[] }[] => {
  const lines = content.split("\n");
  const segments: { text: string; media: MediaItem[] }[] = [];
  let currentTextLines: string[] = [];
  let pendingMedia: MediaItem[] = [];

  const isIndented = (line: string) => /^\s{2,}/.test(line);

  const nextNonEmptyLine = (fromIndex: number): string | null => {
    for (let j = fromIndex; j < lines.length; j++) {
      if (lines[j].trim() !== "") return lines[j];
    }
    return null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const media = extractMedia(line);
    currentTextLines.push(line);

    if (media.length > 0) {
      pendingMedia.push(...media);
    }

    if (pendingMedia.length > 0) {
      const next = nextNonEmptyLine(i + 1);
      if (!next || !isIndented(next)) {
        segments.push({ text: currentTextLines.join("\n"), media: pendingMedia });
        currentTextLines = [];
        pendingMedia = [];
      }
    }
  }

  if (currentTextLines.length > 0 || pendingMedia.length > 0) {
    segments.push({ text: currentTextLines.join("\n"), media: pendingMedia });
  }

  return segments;
};

const Insight = ({
  insights,
  setInsights,
  type,
  hideGenerateButton,
  loading,
  generateInsight,
  accountId,
  initialInsight,
  initialPrompt,
  error,
  collapsible = false,
}: Props) => {
  const [openPromptDropdown, setOpenPromptDropdown] = useState<boolean>();
  const [showDescriptionIndex, setShowDescriptionIndex] = useState<number | null>(null);
  const [copiedPromptIndex, setCopiedPromptIndex] = useState<number | null>(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const customInputRef = useRef<HTMLDivElement>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const streaming = loading && insights && insights.length > 0;

  const handleInsightLinkClick = useCallback(
    (e: React.MouseEvent, segmentMedia: MediaItem[]) => {
      const anchor = (e.target as HTMLElement).closest("a");
      if (!anchor || segmentMedia.length === 0) return;
      const href = anchor.getAttribute("href");
      if (!href) return;

      const match = segmentMedia.find(
        (m) => m.url === href || href.includes(m.url) || m.url.includes(href),
      );
      if (!match) return;

      const thumbnails = document.querySelectorAll<HTMLElement>("[data-media-url]");
      for (const thumb of thumbnails) {
        const thumbUrl = thumb.getAttribute("data-media-url") ?? "";
        if (
          thumbUrl === match.url ||
          thumbUrl.includes(match.url) ||
          match.url.includes(thumbUrl)
        ) {
          e.preventDefault();
          thumb.click();
          return;
        }
      }
    },
    [],
  );

  const [lastUsedPrompt, setLastUsedPrompt] = useState<string | null>(null);
  const [lastUsedPromptId, setLastUsedPromptId] = useState<string | null>(null);
  const [outdatedInsight, setOutdatedInsight] = useState<boolean>(false);
  const [hasGeneratedInsights, setHasGeneratedInsights] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(false);

  // Get prompts from store
  const prompts = useGlobalStore((state) => state.insightPrompts[type] || []);
  const setInsightPrompts = useGlobalStore((state) => state.setInsightPrompts);

  // Fetch prompts only if not already available in store
  useEffect(() => {
    const loadPrompts = async () => {
      if (!accountId) return;

      // Check if prompts are already available in store
      if (prompts.length > 0) {
        return; // Don't fetch if already available
      }

      try {
        setPromptsLoading(true);
        const fetchedPrompts = await fetchInsightPrompts(accountId, type);
        setInsightPrompts(type, fetchedPrompts);
      } catch (error) {
        console.error("Error fetching insight prompts:", error);
      } finally {
        setPromptsLoading(false);
      }
    };
    void loadPrompts();
  }, [accountId, prompts.length, setInsightPrompts, type]);

  const handleGenerateInsight = (promptId?: string, promptText?: string) => {
    if (promptId) {
      generateInsight({ prompt_id: promptId });
      setLastUsedPromptId(promptId);
      setLastUsedPrompt(null);
    } else {
      const promptToUse = promptText || lastUsedPrompt || prompts[0]?.user_prompt;
      generateInsight({ prompt: promptToUse });
      setLastUsedPrompt(promptToUse);
      setLastUsedPromptId(null);
    }
    setOutdatedInsight(false);
    setHasGeneratedInsights(true);
    setExpanded(true);
  };

  // Handle click outside to close custom input
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        customInputRef.current &&
        !customInputRef.current.contains(event.target as Node)
      ) {
        setShowCustomInput(false);
      }
    };

    if (showCustomInput) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomInput]);

  const isLastUsedPrompt = (promptId?: string, promptText?: string) => {
    if (promptId && lastUsedPromptId) {
      return lastUsedPromptId === promptId;
    }
    if (promptText && lastUsedPrompt) {
      return lastUsedPrompt === promptText;
    }
    // For initial state, check against first prompt
    if (!lastUsedPrompt && !lastUsedPromptId && prompts[0]) {
      return promptId === prompts[0].id || promptText === prompts[0].user_prompt;
    }
    return false;
  };

  const isRegenerationCase = hasGeneratedInsights || (insights && insights.length > 0);

  // sync initial states
  useEffect(() => {
    // Sync insight content
    if (initialInsight) {
      setInsights([initialInsight]);
      setHasGeneratedInsights(true);
    }
    // Sync prompt states
    if (initialPrompt && prompts.length > 0) {
      const promptExists = prompts.find((p) => p.id === initialPrompt);
      if (promptExists) {
        // It's a prompt ID
        setLastUsedPromptId(initialPrompt);
        setLastUsedPrompt(null);
      } else {
        // It's a custom prompt
        setCustomPrompt(initialPrompt);
        setLastUsedPrompt(initialPrompt);
        setLastUsedPromptId(null);
      }
    }
    // Sync outdated state
    setOutdatedInsight(error === "stale_insight");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInsight, initialPrompt, error, prompts]);

  const [expanded, setExpanded] = useState<boolean>(!collapsible);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const MAX_LINES = 8;

  useLayoutEffect(() => {
    if (collapsible && contentRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(contentRef.current).lineHeight);
      const maxHeight = lineHeight * MAX_LINES;
      setIsOverflowing(contentRef.current.scrollHeight > maxHeight + 25);
    }
  }, [insights, loading, collapsible]);

  return (
    <div
      className={`${loading || (insights && insights.length > 0) ? "border-[1px]  border-border-gray rounded-[12px] py-5 px-4 mt-4 bg-white pb-4 mb-16" : "mt-4 mb-14"}`}
    >
      {(streaming || (hasGeneratedInsights && !loading)) && (
        <div
          ref={contentRef}
          className="mb-4 transition-all duration-300 ease-in-out"
          style={
            collapsible && !expanded && isOverflowing
              ? {
                  display: "-webkit-box",
                  WebkitLineClamp: MAX_LINES,
                  WebkitBoxOrient: "vertical" as const,
                  overflow: "hidden",
                }
              : undefined
          }
        >
          {insights.map((insight, insightIdx) => {
            const segments = splitContentAtMediaLines(normalizeInsightMarkdown(insight));
            return (
              <div key={insightIdx} className="mb-2 last:mb-0">
                {segments.map((segment, segIdx) => (
                  <div key={segIdx}>
                    {stripMediaImages(segment.text).trim() && (
                      <div onClick={(e) => handleInsightLinkClick(e, segment.media)}>
                        <MarkdownFormatter
                          content={stripMediaImages(segment.text)}
                          className="insight-prose pr-16 pt-1 pb-1 pl-2"
                        />
                      </div>
                    )}
                    {segment.media.length > 0 && (
                      <div className="mt-2 mb-3 pl-8">
                        <FileAttachmentsPreview
                          files={convertMediaToFiles(
                            segment.media,
                            `${insightIdx}-${segIdx}-`,
                          )}
                          className="flex flex-wrap gap-2"
                          showFileName
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
      {loading && !streaming && (
        <div className="flex flex-col gap-2 bg-white">
          <Skeleton className="h-4 w-[90%] !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
          <Skeleton className="h-4 w-[90%] !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
          <Skeleton className="h-4 w-[90%] !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
          <Skeleton className="h-4 w-[60%] !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
          <Skeleton className="h-4 w-[90%] mt-4 !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
          <Skeleton className="h-4 w-[90%] !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
          <Skeleton className="h-4 w-[90%] !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
          <Skeleton className="h-4 w-[60%] !bg-[linear-gradient(90deg,_#FEF4F1_0%,_#FDEFF5_42.79%,_#EDF8FC_100%)]" />
        </div>
      )}

      {/* Generate Insights Button */}
      {!hideGenerateButton && !streaming && !loading && (
        <div className="flex justify-start items-center gap-[15px]">
          <div className="relative flex">
            {collapsible && insights && insights.length > 0 && isOverflowing && (
              <SqButton
                bgColor="white"
                hoverBgColor="#FBFBFB"
                shadow={"sm"}
                hasOutline
                size={"md"}
                className="w-[120px] flex justify-start mr-2 h-8"
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? (
                  <IconChevronUp size={18} color="#02185999" />
                ) : (
                  <IconChevronDown size={18} color="#02185999" />
                )}
                <span className="text-brand-primary-800 font-normal not-italic text-[12px] leading-[19.16px] tracking-[0.02em]">
                  {expanded ? "Show less" : "Show more"}
                </span>
              </SqButton>
            )}
            <div
              className={`flex items-center pr-1 squircle squircle-smooth-[1] squircle-radius-[8px] drop-shadow-sm relative squircle-fill-[white] hover:squircle-fill-[#FBFBFB] before:absolute before:inset-0 before:squircle before:squircle-radius-[8px] before:squircle-outline-[1px] before:squircle-outline-color-[#E7E8E9] before:pointer-events-none`}
            >
              <Button
                variant="default"
                size="sm"
                className={`h-8 bg-transparent hover:bg-transparent pl-[12px] pr-[6px] py-0 rounded-l-[8px] rounded-r-none flex items-center gap-2 border-0 shadow-none`}
                disabled={loading || promptsLoading}
                onClick={() => {
                  if (lastUsedPromptId) {
                    handleGenerateInsight(lastUsedPromptId);
                  } else if (lastUsedPrompt) {
                    handleGenerateInsight(undefined, lastUsedPrompt);
                  } else {
                    handleGenerateInsight(prompts[0]?.id, prompts[0]?.user_prompt);
                  }
                }}
              >
                <svg width="0" height="0" style={{ position: "absolute" }}>
                  <defs>
                    <linearGradient
                      id="insights-ai-gradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stopColor="#FF4500" />
                      <stop offset="100%" stopColor="#FF4699" />
                    </linearGradient>
                  </defs>
                </svg>
                <RiGeminiFill
                  size={14}
                  style={{
                    fill: isRegenerationCase
                      ? "rgba(154, 163, 189, 1)"
                      : "url(#insights-ai-gradient)",
                  }}
                />
                <span
                  className={`overflow-hidden text-ellipsis text-[12px] font-medium leading-[19.16px] tracking-[0.02em] relative top-[1px] ${
                    isRegenerationCase ? "text-brand-primary-800" : "text-[#35467A]"
                  }`}
                  style={{
                    fontWeight: 500,
                    fontStyle: "normal",
                    fontSize: "12px",
                    lineHeight: "19.16px",
                    letterSpacing: "0.02em",
                  }}
                >
                  {isRegenerationCase ? "Re-generate Insights" : "Generate Insights"}
                </span>
              </Button>

              <DropdownMenu
                open={openPromptDropdown}
                onOpenChange={(open) => {
                  setOpenPromptDropdown(open);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <SqButton
                    variant="fill"
                    bgColor="#F0F1F5"
                    className={`squircle-mask h-[26px] w-[26px] px-[8px] py-[6px] flex items-center shadow-none border-0 bg-transparent hover:bg-transparent`}
                    disabled={loading}
                  >
                    <RiPencilLine size={14} color="#9AA3BD" className="shrink-0" />
                  </SqButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  alignOffset={160}
                  side="top"
                  className="w-[350px] bg-white rounded-[12px]"
                >
                  {promptsLoading ? (
                    <div className="p-4 text-center text-sm text-brand-primary-800">
                      Loading prompts...
                    </div>
                  ) : (
                    prompts.map((prompt, index) => (
                      <DropdownMenuItem
                        key={prompt.id || index}
                        className="w-full flex items-start gap-2 cursor-pointer rounded-[8px]"
                        onClick={() => {
                          handleGenerateInsight(prompt.id, prompt.user_prompt);
                          setOpenPromptDropdown(false);
                        }}
                      >
                        <RiGeminiLine
                          size={12}
                          className={`shrink-0 mt-1 ${isLastUsedPrompt(prompt.id) ? "text-brand-orange" : "text-brand-primary-new/70"}`}
                        />
                        <div className="w-full flex flex-col gap-[6px]">
                          <div className="w-full flex justify-between items-center">
                            <p className="text-[#021859B2] text-xs font-[500] leading-5">
                              {prompt.title}
                            </p>
                            <TooltipProvider>
                              <Tooltip open={showDescriptionIndex === index}>
                                <TooltipTrigger>
                                  <div
                                    className="group shrink-0 p-[4px] bg-[#F6F6F5] hover:bg-white rounded-[4px] flex items-center justify-center cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void navigator.clipboard.writeText(
                                        prompt.user_prompt,
                                      );
                                      setCopiedPromptIndex(index);
                                      setTimeout(() => {
                                        setCopiedPromptIndex(null);
                                      }, 2000);
                                    }}
                                    onMouseEnter={() => {
                                      setShowDescriptionIndex(index);
                                    }}
                                    onMouseLeave={() => {
                                      setShowDescriptionIndex(null);
                                    }}
                                  >
                                    <LuCode
                                      className={`w-[12px] h-[12px] text-brand-primary-new/70 ${
                                        copiedPromptIndex === index
                                          ? ""
                                          : " group-hover:hidden"
                                      }`}
                                    />
                                    {copiedPromptIndex !== index && (
                                      <LuCopy className="w-[12px] h-[12px] text-brand-orange hidden group-hover:block" />
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  sideOffset={27}
                                  align="start"
                                >
                                  <p className="max-w-[300px] whitespace-pre-wrap">
                                    {copiedPromptIndex === index
                                      ? "Copied to clipboard!"
                                      : prompt.user_prompt}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <p className="text-[rgba(2,24,89,0.70)] text-[12px] font-normal leading-[16px]">
                            {prompt.description}
                          </p>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                  <DropdownMenuItem
                    className="group w-full flex items-start gap-2 cursor-pointer rounded-[8px]"
                    onClick={() => {
                      setShowCustomInput(true);
                    }}
                  >
                    <div className="w-3 h-3 mt-1 shrink-0 flex items-center justify-center">
                      <RiExpandLeftRightLine
                        size={16}
                        className={`shrink-0 ${isLastUsedPrompt(undefined, customPrompt) ? "text-brand-orange" : "text-brand-primary-new/70"}`}
                      />
                    </div>
                    <div>
                      <span className="text-brand-primary-new/70 text-xs font-[500] leading-5">
                        Custom
                      </span>
                      <p className="text-[rgba(2,24,89,0.70)] text-[12px] font-normal leading-[16px]">
                        Create something entirely new and untested{" "}
                      </p>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {/* Custom Input Component */}
            {showCustomInput && (
              <div
                ref={customInputRef}
                className="z-50 absolute bottom-0 right-0 translate-x-[calc(100%+12px)] p-[10px] border rounded-md shadow-sm bg-white w-[385px]"
              >
                <div className="space-y-4">
                  <Textarea
                    placeholder="Custom"
                    className="min-h-[100px] resize-none text-[14px] text-brand-primary-800 placeholder:text-[#67749B99]"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button
                      disabled={customPrompt.length === 0}
                      onClick={() => {
                        handleGenerateInsight(undefined, customPrompt);
                        setShowCustomInput(false);
                        setOpenPromptDropdown(false);
                      }}
                      className="bg-brand-orange hover:bg-brand-orange/90 text-white border-0"
                    >
                      Generate
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {outdatedInsight && (
            <div className=" text-brand-orange text-sm flex items-center gap-2">
              <LucideAlertCircle className="w-4 h-4 shrink-0" />
              <span className="text-[#FF4500] text-xs font-normal leading-tight tracking-[0.28px] relative top-[1px]">
                The configurations have been updated. Please regenerate the insights to
                reflect the latest data.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Insight;
